import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';
import { INPUT_THEMES, type InputTheme } from '@/components/ui/form/inputThemes';

type BusinessType = Tables<'business_types'>;

interface BusinessTypeAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputStyle?: React.CSSProperties;
  /** Visual theme. Defaults to 'dark' to keep backward-compatibility. */
  theme?: InputTheme;
}

export function BusinessTypeAutocomplete({
  value,
  onChange,
  placeholder = 'Search for business type...',
  className,
  inputStyle,
  theme = 'dark',
}: BusinessTypeAutocompleteProps) {
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const [filteredBusinessTypes, setFilteredBusinessTypes] = useState<BusinessType[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const tk = INPUT_THEMES[theme];

  useEffect(() => {
    const fetchBusinessTypes = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('business_types')
          .select('*')
          .order('name');
        if (error) console.error('Error fetching business types:', error);
        else setBusinessTypes(data || []);
      } catch (error) {
        console.error('Error fetching business types:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinessTypes();
  }, []);

  useEffect(() => {
    if (!value) setFilteredBusinessTypes(businessTypes);
    else
      setFilteredBusinessTypes(
        businessTypes.filter(b => b.name.toLowerCase().includes(value.toLowerCase()))
      );
  }, [value, businessTypes]);

  const handleSelect = (businessType: BusinessType) => {
    onChange(businessType.name);
    setOpen(false);
  };

  const handleInputChange = (inputValue: string) => {
    onChange(inputValue);
    setOpen(true);
  };

  return (
    <div className="relative">
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={e => handleInputChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        className={[tk.input, 'w-full px-4', className ?? ''].filter(Boolean).join(' ')}
        style={inputStyle}
      />

      {open && (
        <div
          className={[
            'absolute z-50 w-full mt-1 rounded-md shadow-lg max-h-60 overflow-auto',
            tk.dropdown,
          ].join(' ')}
        >
          {loading ? (
            <div className={['p-4 text-center', tk.helperText].join(' ')}>
              Loading business types...
            </div>
          ) : filteredBusinessTypes.length === 0 ? (
            <div className={['p-4 text-center', tk.helperText].join(' ')}>
              No business types found
            </div>
          ) : (
            <div className="py-1">
              {filteredBusinessTypes.map(businessType => (
                <div
                  key={businessType.id}
                  className={['flex items-center px-4 py-2', tk.dropdownItem].join(' ')}
                  onClick={() => handleSelect(businessType)}
                >
                  <span className={tk.dropdownItemText}>{businessType.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
